import { useEffect } from 'react';

/**
 * Fetches /api/page-builder/theme on mount and applies the configured colors,
 * fonts, and custom CSS as :root CSS variables. Components opt in by reading
 * those variables (e.g. `var(--theme-header-bg, #1d3aae)`) — that way every
 * value is live-editable from the page builder without code changes.
 *
 * Variables exposed:
 *   --theme-primary, --theme-accent
 *   --theme-header-bg, --theme-header-text
 *   --theme-nav-bg,    --theme-nav-text,    --theme-nav-link-hover
 *   --theme-footer-bg, --theme-footer-text
 *   --theme-body-bg,   --theme-body-text
 *   --theme-button-bg, --theme-button-text
 *   --theme-font-heading, --theme-font-body
 *
 * Renders nothing.
 */
const VAR_MAP = {
  primary_color:   '--theme-primary',
  accent_color:    '--theme-accent',
  header_bg:       '--theme-header-bg',
  header_text:     '--theme-header-text',
  nav_bg:          '--theme-nav-bg',
  nav_text:        '--theme-nav-text',
  nav_link_hover:  '--theme-nav-link-hover',
  footer_bg:       '--theme-footer-bg',
  footer_text:     '--theme-footer-text',
  body_bg:         '--theme-body-bg',
  body_text:       '--theme-body-text',
  button_bg:       '--theme-button-bg',
  button_text:     '--theme-button-text',
  font_heading:    '--theme-font-heading',
  font_body:       '--theme-font-body',
};

export default function ThemeProvider() {
  useEffect(() => {
    let cancelled = false;
    fetch('/api/page-builder/theme')
      .then(r => r.ok ? r.json() : null)
      .then(t => {
        if (cancelled || !t) return;
        const root = document.documentElement;
        for (const [col, cssVar] of Object.entries(VAR_MAP)) {
          if (t[col]) root.style.setProperty(cssVar, t[col]);
        }
        // Inject custom CSS as a <style> tag the admin can override anything with.
        if (t.custom_css) {
          let el = document.getElementById('theme-custom-css');
          if (!el) {
            el = document.createElement('style');
            el.id = 'theme-custom-css';
            document.head.appendChild(el);
          }
          el.textContent = t.custom_css;
        }
        // Announcement bar — render via a portal-ish DOM node injected before <body> children.
        if (t.announcement_text) {
          let bar = document.getElementById('theme-announcement');
          if (!bar) {
            bar = document.createElement('div');
            bar.id = 'theme-announcement';
            document.body.insertBefore(bar, document.body.firstChild);
          }
          bar.textContent = t.announcement_text;
          bar.style.cssText = `
            background: ${t.announcement_bg || '#0d5c3a'};
            color: ${t.announcement_color || '#ffffff'};
            padding: 6px 16px; text-align: center; font-size: 13px;
            font-weight: 600; letter-spacing: 0.02em;
          `;
        }
      })
      .catch(() => {});

    // Re-fetch when the page builder iframe signals a publish (postMessage)
    function onMessage(e) {
      if (e?.data?.type === 'theme:reload') {
        // Re-run the effect by reloading; cheap since iframe is already isolated
        window.location.reload();
      }
    }
    window.addEventListener('message', onMessage);
    return () => { cancelled = true; window.removeEventListener('message', onMessage); };
  }, []);

  return null;
}
